import { useCallback } from 'react';

import { Trash } from 'lucide-react';

import { Button } from '@/shadcn/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

import { AvatarComponent } from '@/components/ui/avatar/avatar';
import { TestId } from '@/types/testId';

type Props = {
  participant: any;
  participantAccountId?: string | null;
  removeGroupMembers?: (accountIds: string[], title: string) => void;
};

export const ParticipantCard = ({
  participant,
  participantAccountId = null,
  removeGroupMembers
}: Props) => {
  const { avatar, name, email, pending, inviteExpired } = participant;

  const remove = useCallback(() => {
    if (!(removeGroupMembers && participantAccountId)) return;
    removeGroupMembers([participantAccountId], name);
  }, [participantAccountId, removeGroupMembers]);

  return (
    <div className="flex flex-row flex-nowrap items-center justify-between rounded-md border border-border bg-card px-5 py-4 text-card-foreground">
      <div className="flex min-w-3/10 grow flex-row flex-nowrap items-center">
        <AvatarComponent name={name} source={avatar} />
        <div className="ml-2.5 flex flex-col flex-nowrap items-start overflow-hidden">
          <div className="truncate text-sm capitalize">{name}</div>
          <a
            className="text-muted-foreground text-xs no-underline hover:underline"
            href={`mailto:${email}`}
          >
            {email}
          </a>
        </div>
      </div>
      <div className="flex shrink-0 flex-row flex-nowrap items-center gap-2 pl-5">
        {pending && (
          <div className="select-none text-muted-foreground text-xs">
            {inviteExpired ? 'Invite expired' : 'Pending invite'}
          </div>
        )}
        {participantAccountId && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="normal-case"
                  data-testid={TestId.ParticipantCard.RemoveButton}
                  onClick={remove}
                  size="icon"
                  variant="ghostDestructiveSubtle"
                >
                  <Trash />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Remove participant</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
};
